export const OPPORTUNITY_STAGES = [
	'Lead',
	'Tender/Hard Bid',
	'Tender',
	'RFP',
	'RFQ',
	'Estimate',
	'Awarded',
];

export const STAGE_AWARDED = 'Awarded';

export const CURVE_METHOD_OPTIONS = [
	{ value: 'scurve', text: 'S-Curve' },
	{ value: 'linear', text: 'Linear' },
	{ value: 'bell', text: 'Bell Curve' },
];

export const S_CURVE_STEEPNESS_OPTIONS = [
	{ value: 6, text: '6 - Gradual' },
	{ value: 8, text: '8 - Standard' },
	{ value: 11, text: '11 - Steep' },
];

export const PROJECT_COLORS = [
	'#185FA5', '#1D9E75', '#D85A30', '#7F77DD',
	'#BA7517', '#993556', '#3B6D11', '#0C447C'
];

export function generateSCurve(n, k, contractValue) {
	const raw = [];
	for (let i = 0; i < n; i++) {
		const x = (i / (n - 1)) * 2 - 1;
		raw.push(1 / (1 + Math.exp(-k * x)));
	}
	const cumulMin = raw[0];
	const cumulMax = raw[n - 1];
	const cumNorm = raw.map(v => (v - cumulMin) / (cumulMax - cumulMin));
	const monthly = cumNorm.map((v, i) => (i === 0 ? v : v - cumNorm[i - 1]));
	return monthly.map(p => p * contractValue);
}

export function generateLinear(n, contractValue) {
	return Array(n).fill(contractValue / n);
}

export function generateBell(n, contractValue) {
	const mean = (n - 1) / 2;
	const std = n / 5;
	const raw = [];
	for (let i = 0; i < n; i++) {
		raw.push(Math.exp(-0.5 * Math.pow((i - mean) / std, 2)));
	}
	const sum = raw.reduce((a, b) => a + b, 0);
	return raw.map(v => (v / sum) * contractValue);
}

export function getMonthKey(startYYYYMM, offsetMonths) {
	const [y, m] = startYYYYMM.split('-').map(Number);
	const d = new Date(y, m - 1 + offsetMonths);
	const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
	const label = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
	return { key, label };
 
}

export function fmt(value) {
	if (value >= 1e6) return '$' + (value / 1e6).toFixed(2) + 'M';
	if (value >= 1e3) return '$' + Math.round(value / 1e3).toLocaleString() + 'K';
	return '$' + Math.round(value).toLocaleString();
}

export function fn_applyFeePct(revenue, feePct) {
	const pct = Number(feePct);
	if (!Number.isFinite(pct)) return revenue;
	return revenue * (pct / 100);
}

export const REVENUE_VIEW_CONTRACT = 'contract';
export const REVENUE_VIEW_FEE = 'fee';

export function fn_projectTotalValue(project, revenueView = REVENUE_VIEW_CONTRACT) {
	const contractValue = Number(project.contractValue) || 0;
	if (revenueView === REVENUE_VIEW_FEE) {
		return fn_applyFeePct(contractValue, project.feePct != null ? project.feePct : 0);
	}
	return contractValue;
}

function clampFeePct(n) {
	return Math.max(0, Math.min(100, Number(n) || 0));
}

function clampDuration(n) {
	return Math.max(2, Math.min(120, Math.round(Number(n) || 2)));
}

function generateMonthlyRevenues(project) {
	const n = clampDuration(project.durationMonths);
	const contractValue = Number(project.contractValue) || 0;
	switch (project.curveMethod) {
		case 'linear':
			return generateLinear(n, contractValue);
		case 'bell':
			return generateBell(n, contractValue);
		case 'scurve':
		default:
			return generateSCurve(n, project.sCurveSteepness || 8, contractValue);
	}
}

export function buildMonthlySchedule(project) {
	const n = clampDuration(project.durationMonths);
	const contractValue = Number(project.contractValue) || 0;
	const monthly = generateMonthlyRevenues(project);
	let cumulative = 0;
	return monthly.map((revenue, i) => {
		const { key, label } = getMonthKey(project.startDate, i);
		cumulative += revenue;
		return {
			monthKey: key,
			monthLabel: label,
			revenue,
			pctOfContract: contractValue ? (revenue / contractValue) * 100 : 0,
			cumulative,
			cumulativePct: contractValue ? (cumulative / contractValue) * 100 : 0,
		};
	});
}

export function buildProjectRevenueMap(project, revenueView = REVENUE_VIEW_CONTRACT) {
	const map = {};
	const feePct = project.feePct != null ? project.feePct : 0;
	buildMonthlySchedule(project).forEach(row => {
		map[row.monthKey] = revenueView === REVENUE_VIEW_FEE
			? fn_applyFeePct(row.revenue, feePct)
			: row.revenue;
	});
	return map;
}

export function buildPortfolioTimeline(projects) {
	const keys = new Set();
	(projects || []).forEach(p => {
		buildMonthlySchedule(p).forEach(row => keys.add(row.monthKey));
	});
	return [...keys].sort();
}

export function monthKeyFromGanttMonth(mo) {
	return `${mo.year}-${String(mo.month + 1).padStart(2, '0')}`;
}

export function fn_forecastEndDate(project) {
	const schedule = buildMonthlySchedule(project);
	if (!schedule.length) return project.endDate || project.startDateFull || `${project.startDate}-01`;
	const [y, m] = schedule[schedule.length - 1].monthKey.split('-').map(Number);
	const d = new Date(y, m, 0);
	return `${y}-${String(m).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function fn_forecastStartDateFull(project) {
	return project.startDateFull || `${project.startDate}-01`;
}

function parseDate(str) {
	if (!str) return null;
	return new Date(str + 'T12:00:00');
}

function toFirstOfMonth(d) {
	return new Date(d.getFullYear(), d.getMonth(), 1);
}

function toLastOfMonth(d) {
	return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

export function fn_filterForecastProjects(projects, filter = {}) {
	const {
		fromDate,
		toDate,
		selectedStages = [],
		allStages = true,
		selectedStatuses = [],
		allStatuses = true,
		showOpportunities = true,
		showProjects = true,
		winProbability = 10,
	} = filter;
	const filterStart = parseDate(fromDate);
	const filterEnd = parseDate(toDate);
	

	let filteredProjects = projects.filter(item => {
		if (item.type === 'opportunity' && !showOpportunities) return false;
		if (item.type === 'project' && !showProjects) return false;
		if (!allStages && selectedStages.length && !selectedStages.includes(item.stage)) return false;
		if (!allStatuses && selectedStatuses.length && !selectedStatuses.includes(item.statusName)) return false;

		if (!filterStart || !filterEnd || isNaN(filterStart) || isNaN(filterEnd)) return true;

		const itemStart = parseDate(fn_forecastStartDateFull(item));
		const itemEnd = parseDate(fn_forecastEndDate(item));
		if (!itemStart || !itemEnd) return false;
		
		return !(itemEnd < filterStart || itemStart > filterEnd);
		
		if (item.type === 'opportunity' && ((item.winProbability)<winProbability.min || (item.winProbability)>winProbability.max))
			return false;
		
		return true;
	});
	
	return filteredProjects;
}

export function fn_buildDateRangeMonthKeys(fromDate, toDate) {
	const months = [];
	const from = parseDate(fromDate);
	const to = parseDate(toDate);
	if (!from || !to || isNaN(from) || isNaN(to)) return months;

	const minDate = toFirstOfMonth(from);
	const maxDate = toLastOfMonth(to);
	const current = new Date(minDate.getFullYear(), minDate.getMonth(), 1);

	while (current <= maxDate) {
		const yr = current.getFullYear();
		const mo = current.getMonth();
		months.push(`${yr}-${String(mo + 1).padStart(2, '0')}`);
		current.setMonth(current.getMonth() + 1);
	}
	return months;
}

export function fn_stageOptionsForProjects(projects) {
	const stages = new Set((projects || []).map(x => x.stage).filter(x => x != null));
	return stages||[];
}
export function fn_statusOptionsForProjects(projects) {
	const statuses = new Set((projects || []).map(x => x.statusName).filter(x => x != null));
	return statuses||[];
}
function countActiveMonths(startDate, endDate) {
	const start = parseDate(startDate);
	const end = parseDate(endDate);
	if (!start || !end || isNaN(start) || isNaN(end)) return 12;
	let count = 0;
	const current = new Date(start.getFullYear(), start.getMonth(), 1);
	const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
	while (current <= endMonth) {
		count++;
		current.setMonth(current.getMonth() + 1);
	}
	return Math.max(2, Math.min(120, count));
}

function toStartYYYYMM(dateStr) {
	const d = parseDate(dateStr);
	if (!d || isNaN(d)) {
		const yr = new Date().getFullYear();
		return `${yr}-01`;
	}
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function fn_buildDummyGanttItems(yr) {
	yr = yr || new Date().getFullYear();
	return [
		{ id: 1, name: 'James Street', type: 'opportunity', startDate: `${yr}-01-01`, endDate: `${yr}-12-31`, stage: 'RFP', monthlyRevenue: 5000000, feePct: 4.5 },
		{ id: 2, name: 'Harbor Point', type: 'project', startDate: `${yr}-03-01`, endDate: `${yr + 1}-02-28`, stage: 'Estimate', monthlyRevenue: 3500000, feePct: 5 },
		{ id: 3, name: 'Oakwood Plaza', type: 'opportunity', startDate: `${yr}-06-01`, endDate: `${yr}-11-30`, stage: 'RFQ', monthlyRevenue: 4200000, feePct: 3.5 },
		{ id: 4, name: 'Riverside Tower', type: 'project', startDate: `${yr}-01-15`, endDate: `${yr}-09-30`, stage: 'Awarded', monthlyRevenue: 6000000, feePct: 6 },
		{ id: 5, name: 'Summit Ridge', type: 'opportunity', startDate: `${yr}-09-01`, endDate: `${yr + 1}-03-31`, stage: 'Lead', monthlyRevenue: 2800000, feePct: 4 },
		{ id: 6, name: 'Lakeview Commons', type: 'project', startDate: `${yr}-04-01`, endDate: `${yr}-12-31`, stage: 'Tender/Hard Bid', monthlyRevenue: 4500000, feePct: 5.5 },
		{ id: 7, name: 'Metro Center', type: 'opportunity', startDate: `${yr}-02-01`, endDate: `${yr}-07-31`, stage: 'Estimate', monthlyRevenue: 3200000, feePct: 3 },
		{ id: 8, name: 'Parkside Village', type: 'project', startDate: `${yr}-10-01`, endDate: `${yr + 1}-03-31`, stage: 'Awarded', monthlyRevenue: 5500000, feePct: 5 },
	];
}

export function fn_ganttItemToForecastProject1(item) {
	const durationMonths = countActiveMonths(item.startDate, item.endDate);
	const contractValue = item.monthlyRevenue * durationMonths;
	return {
		id: String(item.id),
		name: item.name,
		contractValue,
		durationMonths,
		startDate: toStartYYYYMM(item.startDate),
		startDateFull: item.startDate,
		endDate: item.endDate,
		curveMethod: item.stage === STAGE_AWARDED ? 'scurve' : 'bell',
		sCurveSteepness: 8,
		feePct: clampFeePct(item.feePct != null ? item.feePct : 5),
		type: item.type,
		stage: item.stage,
	};
}
export function fn_ganttItemToForecastProject(item) {
	const durationMonths = countActiveMonths(item.startDate, item.endDate);
	//const contractValue = item.monthlyRevenue * durationMonths;
	return {
		id: String(item.id),
		name: item.name,
		contractValue: item.value,
		durationMonths,
		startDate: item.startDate,
		startDateFull: item.startDate,
		endDate: item.endDate,
		curveMethod: item.stage === STAGE_AWARDED ? 'scurve' : 'bell',
		sCurveSteepness: 8,
		feePct: clampFeePct(item.feePct != null ? item.feePct : 5),
		type: item.type,
		stage: item.stage,
		statusName: item.statusName
	};
}


export function fn_buildDummyProjects(yr) {
	return fn_buildDummyGanttItems(yr).map(fn_ganttItemToForecastProject);
}

export function fn_createDefaultProject(ix) {
	const yr = new Date().getFullYear();
	const startDate = `${yr}-01`;
	const durationMonths = 12;
	const project = {
		id: String(Date.now()) + '-' + Math.random().toString(36).slice(2, 7),
		name: 'New Project ' + (ix + 1),
		contractValue: 5000000,
		durationMonths,
		startDate,
		startDateFull: `${yr}-01-01`,
		curveMethod: 'scurve',
		sCurveSteepness: 8,
		feePct: 5,
		type: 'project',
		stage: 'Lead',
	};
	project.endDate = fn_forecastEndDate(project);
	return project;
}
